 if ( typeof InteractiveMap !== 'function' ) {

  class InteractiveMap extends HTMLElement {

    constructor(){
      
      super();

      let initTimer,
          initObserver;
      
      initTimer = setTimeout(()=>{
        this.#init();
        initObserver.unobserve(this);
      }, 10000);

      initObserver = new IntersectionObserver((entries, observer) => {
        if ( ! entries[0].isIntersecting ) {
          return;
        } else {
          this.#init();
          clearInterval(initTimer);
          observer.unobserve(this);
        }
      });
      initObserver.observe(this);

    }
  
    #init(){
        
      if ( typeof google !== 'undefined' && google.maps ) {
        this.#createMap();
      } else {
        if ( window.loadingGoogleMapsScript ) {
          this.ti = setInterval(()=>{
            if ( typeof google !== 'undefined' ) {
              clearInterval(this.ti);
              this.#createMap();
            }
          }, 100);
        } else {
          window.loadingGoogleMapsScript = true;
          const googleScript = document.createElement('script');
          googleScript.onload = (()=>{
            this.#createMap();
          });
          googleScript.async = true;
          googleScript.src = `https://maps.googleapis.com/maps/api/js?v=3&key=${KROWN.settings.google_maps_api_key}&libraries=marker&callback=window.initLocalMap`;
          document.body.appendChild(googleScript);
        
        }

      }
  
    }
  
    #createMap(){

      const mapEl = this.querySelector('[data-js-map-object]');
      const mapMarkers = this.querySelectorAll('[data-js-map-addresses] li');
      const mapId = this.id;
      const resolvedMapId = this.dataset.mapId;
      const useAdvancedMarkers = !!resolvedMapId;
      const useTagPin = this.hasAttribute('data-tag-pin') && useAdvancedMarkers;
      const isSmallScreen = window.matchMedia('(max-width: 767px)').matches;
      const mapOptions = {
        minZoom: 2,
        zoom: parseInt(this.dataset.zoom),
        streetViewControl: false,
        fullscreenControl: false,
        scrollwheel: false,
        panControl: true,
        mapTypeControl: false,
        overviewMapControl: false,
        zoomControl: ! isSmallScreen,
        draggable: true,
        clickableIcons: false,
        gestureHandling: isSmallScreen ? 'cooperative' : 'auto',
        backgroundColor: '#f5f5f3'
      };

      if ( useAdvancedMarkers ) {
        mapOptions.mapId = resolvedMapId;
      } else {
        mapOptions.styles = mapStyles[this.dataset.style];
      }

      const map = new google.maps.Map(document.getElementById(mapEl.id), mapOptions);

      const bounds = new google.maps.LatLngBounds();

      const markerIcons = [];
      const allMarkerElements = [];
      this.markers = {};
      mapMarkers.forEach((elm, i)=>{

        let marker;
        const markerSrc = `${elm.dataset.marker}&id=${i}-${mapId}`;
        const markerWidth = parseInt(elm.dataset.markerWidth, 10) || 48;
        const markerHeight = parseInt(elm.dataset.markerHeight, 10) || 58;

        if ( useAdvancedMarkers ) {

          let markerContent;

          if ( useTagPin ) {
            markerContent = document.createElement('div');
            markerContent.className = 'map-tag-pin';
            markerContent.textContent = elm.dataset.title || '';
          } else {
            markerContent = document.createElement('img');
            markerContent.src = markerSrc;
            markerContent.width = markerWidth;
            markerContent.height = markerHeight;
          }

          marker = new google.maps.marker.AdvancedMarkerElement({
            position: new google.maps.LatLng(elm.dataset.latitude, elm.dataset.longitude),
            content: markerContent,
            map: map,
            gmpClickable: true
          });

          allMarkerElements.push(markerContent);
          bounds.extend(marker.position);

          marker.addEventListener('gmp-click', ()=>{

            this.dispatchEvent(new CustomEvent("marker", {
              detail: {
                store: elm.dataset.id
              }
            }));

            map.panTo(marker.position);

            allMarkerElements.forEach(el=>{
              if ( useTagPin ) {
                el.classList.remove('map-tag-pin--active');
              } else {
                el.classList.remove('scaled-store-icon');
                if ( el.parentElement ) {
                  el.parentElement.style.overflow = "hidden";
                }
              }
            });

            if ( useTagPin ) {
              markerContent.classList.add('map-tag-pin--active');
            } else {
              markerContent.classList.add('scaled-store-icon');
              if ( markerContent.parentElement ) {
                markerContent.parentElement.style.overflow = "visible";
              }
            }

          });

        } else {

          marker = new google.maps.Marker({
            position: new google.maps.LatLng(elm.dataset.latitude, elm.dataset.longitude),
            icon: {
              url: markerSrc,
              scaledSize: new google.maps.Size(markerWidth, markerHeight),
              anchor: new google.maps.Point(markerWidth / 2, markerHeight)
            },
            map: map
          });

          markerIcons.push(markerSrc);
          bounds.extend(marker.position);

          google.maps.event.addListener(marker, 'click', ((marker, elm, _this) => {
            return function() {

              _this.dispatchEvent(new CustomEvent("marker", {
                detail: {
                  store: elm.dataset.id
                }
              }));

              map.panTo(this.getPosition());

              markerIcons.forEach(markerSrc=>{
                const markerEl = document.querySelector(`img[src="${markerSrc}"]`);
                if ( markerEl ) {
                  markerEl.classList.remove('scaled-store-icon');
                  markerEl.parentElement.style.overflow = "hidden";
                }
              });

              const selectedMarker = document.querySelector(`img[src="${marker.icon.url}"]`);
              if ( selectedMarker ) {
                selectedMarker.classList.add('scaled-store-icon');
                selectedMarker.parentElement.style.overflow = "visible";
              }

            }
          })(marker, elm, this));

        }

        this.markers[elm.dataset.id] = marker;
      })

      const fitMapToMarkers = () => {
        google.maps.event.trigger(map, 'resize');

        if ( mapMarkers.length > 1 ) {
          map.fitBounds(bounds, isSmallScreen ? 32 : 64);

          google.maps.event.addListenerOnce(map, "idle", () => {
            const maximumOverviewZoom = isSmallScreen ? 11 : 12;
            if ( map.getZoom() > maximumOverviewZoom ) {
              map.setZoom(maximumOverviewZoom);
            }
          });
        } else if ( mapMarkers.length === 1 ) {
          map.setCenter(bounds.getCenter());
          map.setZoom(parseInt(this.dataset.zoom));
        }
      };

      if ( mapEl.offsetWidth > 0 && mapEl.offsetHeight > 0 ) {
        requestAnimationFrame(fitMapToMarkers);
      } else if ( typeof ResizeObserver === 'function' ) {
        this.mapResizeObserver = new ResizeObserver(entries => {
          if ( entries[0].contentRect.width > 0 && entries[0].contentRect.height > 0 ) {
            this.mapResizeObserver.disconnect();
            requestAnimationFrame(fitMapToMarkers);
          }
        });
        this.mapResizeObserver.observe(mapEl);
      }

      this.dispatchEvent(new CustomEvent("ready"));
  
    }

    triggerMarkerClick(markerId){
      const marker = this.markers[markerId];
      if ( marker ) {
        if ( marker instanceof google.maps.marker.AdvancedMarkerElement ) {
          marker.dispatchEvent(new Event('gmp-click'));
        } else {
          google.maps.event.trigger(marker, 'click');
        }
      }
    }

    disconnectedCallback(){
      if ( this.ti ) {
        clearInterval(this.ti);
      }
      if ( this.mapResizeObserver ) {
        this.mapResizeObserver.disconnect();
      }
    }

    fallback(){
      this.querySelector('[data-js-map-fallback]').style.display = "block";
      this.querySelector('[data-js-map-object]').style.display = "none";
    }
  
  }

  if ( typeof customElements.get('interactive-map') === 'undefined' ) {
    customElements.define('interactive-map', InteractiveMap);
  }

  const mapStyles = {
    standard: [
      {
        "featureType": "poi",
        "stylers": [
          {
            "visibility": "off"
          }
        ]
      }
    ],
    silver: [
      {
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#f5f5f5"
          }
        ]
      },
      {
        "elementType": "labels.icon",
        "stylers": [
          {
            "visibility": "off"
          }
        ]
      },
      {
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#616161"
          }
        ]
      },
      {
        "elementType": "labels.text.stroke",
        "stylers": [
          {
            "color": "#f5f5f5"
          }
        ]
      },
      {
        "featureType": "administrative.land_parcel",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#bdbdbd"
          }
        ]
      },
      {
        "featureType": "poi",
        "stylers": [
          {
            "visibility": "off"
          }
        ]
      },
      {
        "featureType": "road",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#ffffff"
          }
        ]
      },
      {
        "featureType": "road.arterial",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#757575"
          }
        ]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#dadada"
          }
        ]
      },
      {
        "featureType": "road.highway",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#616161"
          }
        ]
      },
      {
        "featureType": "road.local",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#9e9e9e"
          }
        ]
      },
      {
        "featureType": "transit.line",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#e5e5e5"
          }
        ]
      },
      {
        "featureType": "transit.station",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#eeeeee"
          }
        ]
      },
      {
        "featureType": "water",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#c9c9c9"
          }
        ]
      },
      {
        "featureType": "water",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#9e9e9e"
          }
        ]
      }
    ],
    retro: [
      {
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#ebe3cd"
          }
        ]
      },
      {
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#523735"
          }
        ]
      },
      {
        "elementType": "labels.text.stroke",
        "stylers": [
          {
            "color": "#f5f1e6"
          }
        ]
      },
      {
        "featureType": "administrative",
        "elementType": "geometry.stroke",
        "stylers": [
          {
            "color": "#c9b2a6"
          }
        ]
      },
      {
        "featureType": "administrative.land_parcel",
        "elementType": "geometry.stroke",
        "stylers": [
          {
            "color": "#dcd2be"
          }
        ]
      },
      {
        "featureType": "administrative.land_parcel",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#ae9e90"
          }
        ]
      },
      {
        "featureType": "landscape.natural",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#dfd2ae"
          }
        ]
      },
      {
        "featureType": "poi",
        "stylers": [
          {
            "visibility": "off"
          }
        ]
      },
      {
        "featureType": "road",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#f5f1e6"
          }
        ]
      },
      {
        "featureType": "road.arterial",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#fdfcf8"
          }
        ]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#f8c967"
          }
        ]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry.stroke",
        "stylers": [
          {
            "color": "#e9bc62"
          }
        ]
      },
      {
        "featureType": "road.highway.controlled_access",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#e98d58"
          }
        ]
      },
      {
        "featureType": "road.highway.controlled_access",
        "elementType": "geometry.stroke",
        "stylers": [
          {
            "color": "#db8555"
          }
        ]
      },
      {
        "featureType": "road.local",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#806b63"
          }
        ]
      },
      {
        "featureType": "transit.line",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#dfd2ae"
          }
        ]
      },
      {
        "featureType": "transit.line",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#8f7d77"
          }
        ]
      },
      {
        "featureType": "transit.line",
        "elementType": "labels.text.stroke",
        "stylers": [
          {
            "color": "#ebe3cd"
          }
        ]
      },
      {
        "featureType": "transit.station",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#dfd2ae"
          }
        ]
      },
      {
        "featureType": "water",
        "elementType": "geometry.fill",
        "stylers": [
          {
            "color": "#b9d3c2"
          }
        ]
      },
      {
        "featureType": "water",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#92998d"
          }
        ]
      }
    ],
    dark: [
      {
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#212121"
          }
        ]
      },
      {
        "elementType": "labels.icon",
        "stylers": [
          {
            "visibility": "off"
          }
        ]
      },
      {
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#757575"
          }
        ]
      },
      {
        "elementType": "labels.text.stroke",
        "stylers": [
          {
            "color": "#212121"
          }
        ]
      },
      {
        "featureType": "administrative",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#757575"
          }
        ]
      },
      {
        "featureType": "administrative.country",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#9e9e9e"
          }
        ]
      },
      {
        "featureType": "administrative.land_parcel",
        "stylers": [
          {
            "visibility": "off"
          }
        ]
      },
      {
        "featureType": "administrative.locality",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#bdbdbd"
          }
        ]
      },
      {
        "featureType": "poi",
        "stylers": [
          {
            "visibility": "off"
          }
        ]
      },
      {
        "featureType": "road",
        "elementType": "geometry.fill",
        "stylers": [
          {
            "color": "#2c2c2c"
          }
        ]
      },
      {
        "featureType": "road",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#8a8a8a"
          }
        ]
      },
      {
        "featureType": "road.arterial",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#373737"
          }
        ]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#3c3c3c"
          }
        ]
      },
      {
        "featureType": "road.highway.controlled_access",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#4e4e4e"
          }
        ]
      },
      {
        "featureType": "road.local",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#616161"
          }
        ]
      },
      {
        "featureType": "transit",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#757575"
          }
        ]
      },
      {
        "featureType": "water",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#000000"
          }
        ]
      },
      {
        "featureType": "water",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#3d3d3d"
          }
        ]
      }
    ],
    night: [
      {
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#242f3e"
          }
        ]
      },
      {
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#746855"
          }
        ]
      },
      {
        "elementType": "labels.text.stroke",
        "stylers": [
          {
            "color": "#242f3e"
          }
        ]
      },
      {
        "featureType": "administrative.locality",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#d59563"
          }
        ]
      },
      {
        "featureType": "poi",
        "stylers": [
          {
            "visibility": "off"
          }
        ]
      },
      {
        "featureType": "road",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#38414e"
          }
        ]
      },
      {
        "featureType": "road",
        "elementType": "geometry.stroke",
        "stylers": [
          {
            "color": "#212a37"
          }
        ]
      },
      {
        "featureType": "road",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#9ca5b3"
          }
        ]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#746855"
          }
        ]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry.stroke",
        "stylers": [
          {
            "color": "#1f2835"
          }
        ]
      },
      {
        "featureType": "road.highway",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#f3d19c"
          }
        ]
      },
      {
        "featureType": "transit",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#2f3948"
          }
        ]
      },
      {
        "featureType": "transit.station",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#d59563"
          }
        ]
      },
      {
        "featureType": "water",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#17263c"
          }
        ]
      },
      {
        "featureType": "water",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#515c6d"
          }
        ]
      },
      {
        "featureType": "water",
        "elementType": "labels.text.stroke",
        "stylers": [
          {
            "color": "#17263c"
          }
        ]
      }
    ],
    aubergine: [
      {
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#1d2c4d"
          }
        ]
      },
      {
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#8ec3b9"
          }
        ]
      },
      {
        "elementType": "labels.text.stroke",
        "stylers": [
          {
            "color": "#1a3646"
          }
        ]
      },
      {
        "featureType": "administrative.country",
        "elementType": "geometry.stroke",
        "stylers": [
          {
            "color": "#4b6878"
          }
        ]
      },
      {
        "featureType": "administrative.land_parcel",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#64779e"
          }
        ]
      },
      {
        "featureType": "administrative.province",
        "elementType": "geometry.stroke",
        "stylers": [
          {
            "color": "#4b6878"
          }
        ]
      },
      {
        "featureType": "landscape.man_made",
        "elementType": "geometry.stroke",
        "stylers": [
          {
            "color": "#334e87"
          }
        ]
      },
      {
        "featureType": "landscape.natural",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#023e58"
          }
        ]
      },
      {
        "featureType": "poi",
        "stylers": [
          {
            "visibility": "off"
          }
        ]
      },
      {
        "featureType": "road",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#304a7d"
          }
        ]
      },
      {
        "featureType": "road",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#98a5be"
          }
        ]
      },
      {
        "featureType": "road",
        "elementType": "labels.text.stroke",
        "stylers": [
          {
            "color": "#1d2c4d"
          }
        ]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#2c6675"
          }
        ]
      },
      {
        "featureType": "road.highway",
        "elementType": "geometry.stroke",
        "stylers": [
          {
            "color": "#255763"
          }
        ]
      },
      {
        "featureType": "road.highway",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#b0d5ce"
          }
        ]
      },
      {
        "featureType": "road.highway",
        "elementType": "labels.text.stroke",
        "stylers": [
          {
            "color": "#023e58"
          }
        ]
      },
      {
        "featureType": "transit",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#98a5be"
          }
        ]
      },
      {
        "featureType": "transit",
        "elementType": "labels.text.stroke",
        "stylers": [
          {
            "color": "#1d2c4d"
          }
        ]
      },
      {
        "featureType": "transit.line",
        "elementType": "geometry.fill",
        "stylers": [
          {
            "color": "#283d6a"
          }
        ]
      },
      {
        "featureType": "transit.station",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#3a4762"
          }
        ]
      },
      {
        "featureType": "water",
        "elementType": "geometry",
        "stylers": [
          {
            "color": "#0e1626"
          }
        ]
      },
      {
        "featureType": "water",
        "elementType": "labels.text.fill",
        "stylers": [
          {
            "color": "#4e6d70"
          }
        ]
      }
    ]
  }
}
